import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  House, ShieldCheck, FileText, Calendar, Sparkle, SignOut,
  Shield, Database, Bank, Globe, Briefcase, FirstAid, Users, Buildings,
  Bell, Robot, FolderLock, ClipboardText, Crown, GitBranch, ListChecks, Warning,
  CaretDown, CaretRight, ArrowSquareOut, CheckCircle, Plug, Brain, BellRinging, Files, Tag, X
} from '@phosphor-icons/react';
import { useAuth, API } from '@/App';

// ============================================
// NOTIFICATION DRAWER COMPONENT
// Renders as a full-screen overlay with opaque drawer
// Now includes AURA Pending Updates section
// ============================================
const NotificationDrawer = ({ 
  notifications, 
  unreadNotifications, 
  onClose, 
  onMarkRead, 
  onMarkAllRead, 
  onViewAll,
  pendingAuraUpdates,
  onAcceptAuraUpdate,
  onDeclineAuraUpdate,
  acceptingUpdate
}) => {
  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div 
      className="notification-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        isolation: 'isolate'
      }}
    >
      {/* Backdrop - semi-transparent */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.5)',
          zIndex: 99998
        }}
      />
      
      {/* Drawer - FULLY OPAQUE */}
      <div
        className="notification-drawer"
        style={{
          position: 'fixed',
          top: 0,
          left: 272, // After sidebar (256px + 16px gap)
          bottom: 0,
          width: 'min(420px, calc(100vw - 288px))',
          backgroundColor: '#ffffff',
          opacity: 1,
          zIndex: 99999,
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          borderRight: '1px solid #e2e8f0',
          isolation: 'isolate'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div 
          style={{
            padding: '20px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#ffffff'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BellRinging size={22} style={{ color: '#d97706' }} />
              </div>
              <div>
                <h2 style={{ 
                  fontSize: '18px', 
                  fontWeight: 600, 
                  color: '#0f172a',
                  margin: 0
                }}>
                  Notifications
                </h2>
                {unreadNotifications > 0 && (
                  <span style={{ fontSize: '13px', color: '#d97706' }}>
                    {unreadNotifications} unread
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={20} style={{ color: '#64748b' }} />
            </button>
          </div>
          
          {/* Mark All Read Button */}
          {unreadNotifications > 0 && (
            <button
              onClick={onMarkAllRead}
              style={{
                marginTop: '16px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 500,
                color: '#7c3aed',
                backgroundColor: '#f5f3ff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ede9fe'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f5f3ff'}
            >
              <CheckCircle size={16} />
              Mark all as read
            </button>
          )}
        </div>
        
        {/* Notification List - Scrollable with opaque background */}
        <div 
          className="notification-list"
          style={{
            overflowY: 'auto',
            backgroundColor: '#ffffff'
          }}
        >
          {/* AURA Pending Updates Section */}
          {pendingAuraUpdates && pendingAuraUpdates.length > 0 && (
            <div style={{ backgroundColor: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
              <div style={{ 
                padding: '12px 20px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                borderBottom: '1px solid #fed7aa'
              }}>
                <FolderLock size={16} style={{ color: '#ea580c' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#c2410c' }}>
                  AURA Library Updates ({pendingAuraUpdates.length})
                </span>
              </div>
              {pendingAuraUpdates.map((update, index) => (
                <div
                  key={update.id}
                  style={{
                    padding: '14px 20px',
                    borderBottom: index < pendingAuraUpdates.length - 1 ? '1px solid #fed7aa' : 'none',
                    backgroundColor: '#fff7ed'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      backgroundColor: '#ffedd5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Files size={18} style={{ color: '#ea580c' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ 
                        fontSize: '13px', 
                        fontWeight: 500, 
                        color: '#1c1917',
                        margin: 0,
                        lineHeight: 1.4
                      }}>
                        {update.document_name || update.source_document?.name || 'New Document'}
                      </h4>
                      <p style={{ 
                        fontSize: '11px', 
                        color: '#78716c',
                        margin: '4px 0 0 0',
                        lineHeight: 1.4
                      }}>
                        {update.update_type === 'new_version' ? 'Version update available' : 'New document from AURA Master Library'}
                      </p>
                      {update.source_document?.theme && (
                        <span style={{
                          display: 'inline-block',
                          marginTop: '6px',
                          padding: '2px 8px',
                          fontSize: '10px',
                          borderRadius: '4px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e'
                        }}>
                          {update.source_document.theme.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <button
                          onClick={() => onAcceptAuraUpdate(update.id)}
                          disabled={acceptingUpdate === update.id}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#ffffff',
                            backgroundColor: acceptingUpdate === update.id ? '#a3a3a3' : '#16a34a',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: acceptingUpdate === update.id ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {acceptingUpdate === update.id ? (
                            <span>Adding...</span>
                          ) : (
                            <>
                              <CheckCircle size={14} weight="bold" />
                              Accept
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => onDeclineAuraUpdate(update.id)}
                          disabled={acceptingUpdate === update.id}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#dc2626',
                            backgroundColor: 'transparent',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            cursor: acceptingUpdate === update.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Regular Notifications Section */}
          {notifications.length === 0 && (!pendingAuraUpdates || pendingAuraUpdates.length === 0) ? (
            <div style={{ 
              padding: '48px 24px', 
              textAlign: 'center',
              backgroundColor: '#ffffff'
            }}>
              <Bell size={48} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>No notifications yet</p>
              <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>You'll see regulatory updates here</p>
            </div>
          ) : notifications.length > 0 ? (
            <div>
              {pendingAuraUpdates && pendingAuraUpdates.length > 0 && (
                <div style={{ 
                  padding: '12px 20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  borderBottom: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  <Bell size={16} style={{ color: '#64748b' }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                    Other Notifications
                  </span>
                </div>
              )}
              {notifications.slice(0, 30).map((notif, index) => (
                <div
                  key={notif.id}
                  onClick={() => !notif.is_read && onMarkRead(notif.id)}
                  className="notification-item"
                  style={{
                    padding: '16px 20px',
                    backgroundColor: notif.is_read ? '#ffffff' : '#faf5ff',
                    borderBottom: index < notifications.length - 1 ? '1px solid #e2e8f0' : 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseOver={(e) => {
                    if (notif.is_read) e.currentTarget.style.backgroundColor = '#f8fafc';
                    else e.currentTarget.style.backgroundColor = '#f3e8ff';
                  }}
                  onMouseOut={(e) => {
                    if (notif.is_read) e.currentTarget.style.backgroundColor = '#ffffff';
                    else e.currentTarget.style.backgroundColor = '#faf5ff';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {/* Priority indicator */}
                    <div style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      marginTop: 5,
                      flexShrink: 0,
                      backgroundColor: notif.priority === 'urgent' ? '#ef4444' : 
                                       notif.priority === 'high' ? '#f59e0b' : '#8b5cf6'
                    }} />
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                        <h4 style={{ 
                          fontSize: '14px', 
                          fontWeight: 500, 
                          color: '#0f172a',
                          margin: 0,
                          lineHeight: 1.4
                        }}>
                          {notif.title}
                        </h4>
                        {!notif.is_read && (
                          <span style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#8b5cf6',
                            flexShrink: 0
                          }} />
                        )}
                      </div>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#475569',
                        margin: '6px 0 0 0',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {notif.message}
                      </p>
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#94a3b8',
                        marginTop: '8px',
                        display: 'block'
                      }}>
                        {notif.created_at ? new Date(notif.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        
        {/* Footer - Fixed */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc'
        }}>
          <button
            onClick={onViewAll}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#7c3aed',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background-color 0.15s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f3ff'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            View All Compliance Alerts →
          </button>
        </div>
      </div>
    </div>
  );
};

// Accountable Institutions for FICA sub-navigation
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, token } = useAuth();
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(
    location.pathname.startsWith('/diagnostics')
  );
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [pendingAuraUpdates, setPendingAuraUpdates] = useState([]);
  const [acceptingUpdate, setAcceptingUpdate] = useState(null);

  // Fetch notifications count and pending AURA updates
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/notifications/my?unread_only=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const notifs = res.data.notifications || [];
        setNotifications(notifs);
        setUnreadNotifications(notifs.filter(n => !n.is_read).length);
      } catch (error) {
        // Notifications not available - that's fine
        setUnreadNotifications(0);
      }
    };
    
    const fetchPendingAuraUpdates = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/regulatory-library/pending-updates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingAuraUpdates(res.data.requests || []);
      } catch (error) {
        // Pending updates not available - that's fine
        setPendingAuraUpdates([]);
      }
    };
    
    fetchNotifications();
    fetchPendingAuraUpdates();
    // Poll every 60 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchPendingAuraUpdates();
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadNotifications(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read');
    }
  };

  // Handle accepting AURA pending update
  const handleAcceptAuraUpdate = async (requestId) => {
    setAcceptingUpdate(requestId);
    try {
      await axios.post(`${API}/regulatory-library/pending-updates/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from pending list
      setPendingAuraUpdates(prev => prev.filter(u => u.id !== requestId));
    } catch (error) {
      console.error('Failed to accept AURA update:', error);
    } finally {
      setAcceptingUpdate(null);
    }
  };

  // Handle declining AURA pending update
  const handleDeclineAuraUpdate = async (requestId) => {
    setAcceptingUpdate(requestId);
    try {
      await axios.post(`${API}/regulatory-library/pending-updates/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from pending list
      setPendingAuraUpdates(prev => prev.filter(u => u.id !== requestId));
    } catch (error) {
      console.error('Failed to decline AURA update:', error);
    } finally {
      setAcceptingUpdate(null);
    }
  };

// NAVI Avatar URL
const NAVI_AVATAR = "https://customer-assets.emergentagent.com/job_risk-engine-pro-2/artifacts/0rppkc2h_image.png";

  const mainNavItems = [
    { icon: <House size={20} />, label: 'Dashboard', path: '/dashboard' },
    { 
      icon: (
        <div className="w-5 h-5 rounded-full overflow-hidden border border-amber-500/50">
          <img src={NAVI_AVATAR} alt="NAVI" className="w-full h-full object-cover" />
        </div>
      ), 
      label: 'NAVI', 
      path: '/navi' 
    },
    { icon: <ShieldCheck size={20} />, label: 'Regulatory Universe', path: '/regulatory-universe' },
    { icon: <ClipboardText size={20} />, label: 'CRMP', path: '/crmp-v2' },
    { icon: <Sparkle size={20} />, label: 'Change Management', path: '/change-management' },
    { icon: <GitBranch size={20} />, label: 'Workflow', path: '/workflow' },
    { icon: <FileText size={20} />, label: 'Regulatory Library', path: '/library' },
    { icon: <Files size={20} />, label: 'Policy Library', path: '/policy-library' },
    // Diagnostics is now handled separately as expandable
    { icon: <Warning size={20} />, label: 'Risk Log', path: '/risk-log' },
    { icon: <ListChecks size={20} />, label: 'Remediation Tracker', path: '/remediation-tracker' },
    { icon: <Bell size={20} />, label: 'Compliance Alerts', path: '/compliance-alerts' },
    { icon: <Calendar size={20} />, label: 'Audit Plans', path: '/audit-plans' },
    { icon: <Buildings size={20} />, label: 'Company Overview', path: '/company-overview' },
    { icon: <Plug size={20} />, label: 'Integration Hub', path: '/integration-hub' }
  ];
  
  // Items before Diagnostics (first 7 - includes Document Governance and Change Management)
  const beforeDiagnosticsItems = mainNavItems.slice(0, 7);
  // Items after Diagnostics (remaining items - starting from Risk Log)
  const afterDiagnosticsItems = mainNavItems.slice(7);

  // Super Admin only items
  const adminNavItems = [
    { icon: <FolderLock size={20} />, label: 'AURA Master Library', path: '/aura-master-library' },
    { icon: <Tag size={20} />, label: 'Master Library Taxonomy', path: '/master-library-taxonomy' },
    { icon: <Brain size={20} />, label: 'NAVI Logic', path: '/navi-logic' }
  ];

  // Navigate Admin only items
  const navigateAdminItems = [
    { icon: <Crown size={20} />, label: 'Navigate Admin', path: '/navigate-admin' }
  ];

  const themeItems = [
    { icon: <Shield size={20} />, label: 'Cybersecurity', path: '/themes/cybersecurity', color: '#3B82F6' },
    { icon: <Database size={20} />, label: 'Data Protection', path: '/themes/data-protection', color: '#10B981' },
    { icon: <Bank size={20} />, label: 'Financial Crime', path: '/themes/financial-crime', color: '#F59E0B' },
    { icon: <Globe size={20} />, label: 'Tax Compliance', path: '/themes/tax-compliance', color: '#8B5CF6' },
    { icon: <Briefcase size={20} />, label: 'Corporate Governance', path: '/themes/corporate-governance', color: '#EC4899' },
    { icon: <FirstAid size={20} />, label: 'Consumer Protection', path: '/themes/consumer-protection', color: '#14B8A6' },
    { icon: <Users size={20} />, label: 'Employment & Labour', path: '/themes/employment-labour', color: '#F97316' }
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Render nav item
  const renderNavItem = (item) => (
    <motion.button
      key={item.path}
      onClick={() => navigate(item.path)}
      whileHover={{ x: 4 }}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm mb-1 transition-all ${
        isActive(item.path)
          ? 'bg-gold text-gold-foreground'
          : 'text-text-primary hover:bg-background-hover'
      }`}
    >
      {item.icon}
      <span className="text-sm font-medium">{item.label}</span>
    </motion.button>
  );

  return (
    <div className="w-64 h-screen bg-background-surface border-r border-border flex flex-col fixed left-0 top-0">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_risk-engine-pro-2/artifacts/2vz12eng_Asset%203.png"
              alt="Navigate Aura"
              className="w-10 h-10"
            />
            <div>
              <div className="text-sm font-heading font-semibold text-text-primary">
                NAVIGATE AURA
              </div>
              <div className="text-xs text-text-tertiary">
                {user?.company_name}
              </div>
            </div>
          </div>
          
          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => setShowNotificationPanel(!showNotificationPanel)}
              className="p-2 hover:bg-background-hover rounded-lg transition-colors relative"
              data-testid="notification-bell"
            >
              {(unreadNotifications > 0 || pendingAuraUpdates.length > 0) ? (
                <BellRinging size={20} weight="fill" className="text-amber-500" />
              ) : (
                <Bell size={20} className="text-text-tertiary" />
              )}
              {(unreadNotifications + pendingAuraUpdates.length) > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {(unreadNotifications + pendingAuraUpdates.length) > 9 ? '9+' : (unreadNotifications + pendingAuraUpdates.length)}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Notification Drawer - Rendered via Portal at document.body */}
      {showNotificationPanel && createPortal(
        <NotificationDrawer
          notifications={notifications}
          unreadNotifications={unreadNotifications}
          onClose={() => setShowNotificationPanel(false)}
          onMarkRead={markNotificationRead}
          onMarkAllRead={markAllNotificationsRead}
          onViewAll={() => {
            setShowNotificationPanel(false);
            navigate('/compliance-alerts');
          }}
          pendingAuraUpdates={pendingAuraUpdates}
          onAcceptAuraUpdate={handleAcceptAuraUpdate}
          onDeclineAuraUpdate={handleDeclineAuraUpdate}
          acceptingUpdate={acceptingUpdate}
        />,
        document.body
      )}

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <div className="px-3 mb-6">
          <div className="text-xs uppercase tracking-wider text-text-tertiary font-semibold px-3 mb-2">
            Main
          </div>
          
          {/* Items before Diagnostics */}
          {beforeDiagnosticsItems.map(renderNavItem)}
          
          {/* Maturity Assessments - Expandable */}
          <div className="mb-1">
            <motion.button
              onClick={() => setDiagnosticsExpanded(!diagnosticsExpanded)}
              whileHover={{ x: 4 }}
              data-testid="nav-maturity-assessments"
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-sm transition-all ${
                location.pathname.startsWith('/diagnostics')
                  ? 'bg-gold text-gold-foreground'
                  : 'text-text-primary hover:bg-background-hover'
              }`}
            >
              <div className="flex items-center gap-3">
                <ClipboardText size={20} />
                <span className="text-sm font-medium">Maturity Assessments</span>
              </div>
              {diagnosticsExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
            </motion.button>
            
            {/* Maturity Assessments Sub-items */}
            <AnimatePresence>
              {diagnosticsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  {/* Assessments Overview */}
                  <motion.button
                    onClick={() => navigate('/diagnostics')}
                    whileHover={{ x: 4 }}
                    className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 rounded-sm transition-all ${
                      location.pathname === '/diagnostics'
                        ? 'bg-gold/20 text-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>
                    <span className="text-xs font-medium">Assessments Hub</span>
                  </motion.button>
                  
                  {/* Cyber Diagnostics */}
                  <motion.button
                    onClick={() => navigate('/diagnostics/cybersecurity')}
                    whileHover={{ x: 4 }}
                    className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 rounded-sm transition-all ${
                      location.pathname === '/diagnostics/cybersecurity'
                        ? 'bg-gold/20 text-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                    <span className="text-xs font-medium">Cyber Maturity</span>
                  </motion.button>
                  
                  {/* ICT Assessment */}
                  <motion.button
                    onClick={() => navigate('/diagnostics/ict-assessment')}
                    whileHover={{ x: 4 }}
                    data-testid="nav-ict-assessment"
                    className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 rounded-sm transition-all ${
                      location.pathname === '/diagnostics/ict-assessment'
                        ? 'bg-gold/20 text-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                    <span className="text-xs font-medium">ICT Assessment</span>
                  </motion.button>
                  
                  {/* OMNI Readiness - External Link */}
                  <motion.a
                    href="https://conduct-ready.preview.emergentagent.com/auth"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ x: 4 }}
                    className="w-full flex items-center justify-between pl-9 pr-3 py-2 rounded-sm transition-all text-text-secondary hover:text-text-primary hover:bg-background-hover"
                    data-testid="nav-omni-readiness"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                      <span className="text-xs font-medium">OMNI Readiness</span>
                    </div>
                    <ArrowSquareOut size={14} className="text-text-tertiary" />
                  </motion.a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Items after Diagnostics */}
          {afterDiagnosticsItems.map(renderNavItem)}
        </div>

        {/* Admin Section - Super Admin Only */}
        {user?.is_super_admin && (
          <div className="px-3 mb-6">
            <div className="text-xs uppercase tracking-wider text-gold font-semibold px-3 mb-2">
              Admin
            </div>
            {adminNavItems.map((item) => (
              <motion.button
                key={item.path}
                onClick={() => navigate(item.path)}
                whileHover={{ x: 4 }}
                data-testid={`admin-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm mb-1 transition-all ${
                  isActive(item.path)
                    ? 'bg-gold text-gold-foreground'
                    : 'text-text-primary hover:bg-background-hover'
                }`}
              >
                <div className={isActive(item.path) ? '' : 'text-gold'}>
                  {item.icon}
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </motion.button>
            ))}
          </div>
        )}

        {/* Navigate Admin Section - Platform Admins Only */}
        {user?.is_navigate_admin && (
          <div className="px-3 mb-6">
            <div className="text-xs uppercase tracking-wider text-purple-400 font-semibold px-3 mb-2">
              Platform Admin
            </div>
            {navigateAdminItems.map((item) => (
              <motion.button
                key={item.path}
                onClick={() => navigate(item.path)}
                whileHover={{ x: 4 }}
                data-testid={`platform-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm mb-1 transition-all ${
                  isActive(item.path)
                    ? 'bg-purple-500 text-white'
                    : 'text-text-primary hover:bg-background-hover'
                }`}
              >
                <div className={isActive(item.path) ? '' : 'text-purple-400'}>
                  {item.icon}
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </motion.button>
            ))}
          </div>
        )}

        {/* Group Admin Section - Group Admins and Navigate Admins */}
        {(user?.is_group_admin || user?.is_navigate_admin) && (
          <div className="px-3 mb-6">
            <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold px-3 mb-2">
              Group Structures
            </div>
            <motion.button
              onClick={() => navigate('/groups')}
              whileHover={{ x: 4 }}
              data-testid="nav-group-structures"
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm mb-1 transition-all ${
                isActive('/groups')
                  ? 'bg-emerald-500 text-white'
                  : 'text-text-primary hover:bg-background-hover'
              }`}
            >
              <div className={isActive('/groups') ? '' : 'text-emerald-400'}>
                <Buildings size={20} />
              </div>
              <span className="text-sm font-medium">Group Dashboard</span>
            </motion.button>
          </div>
        )}

        {/* Regulatory Themes */}
        <div className="px-3">
          <div className="text-xs uppercase tracking-wider text-text-tertiary font-semibold px-3 mb-2">
            Regulatory Themes
          </div>
          {themeItems.map((item) => (
            <motion.button
              key={item.path}
              onClick={() => navigate(item.path)}
              whileHover={{ x: 4 }}
              data-testid={`theme-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm mb-1 transition-all ${
                isActive(item.path)
                  ? 'bg-gold text-gold-foreground'
                  : 'text-text-primary hover:bg-background-hover'
              }`}
            >
              <div style={{ color: isActive(item.path) ? 'inherit' : item.color }}>
                {item.icon}
              </div>
              <span className="text-sm">{item.label}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        <button
          onClick={() => {
            logout();
            navigate('/');
          }}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-text-secondary hover:text-text-primary hover:bg-background-hover rounded-sm transition-all"
        >
          <SignOut size={20} />
          <span className="text-sm">Logout</span>
        </button>
        
        {/* Platform Branding */}
        <div className="mt-4 pt-3 border-t border-border/50 text-center">
          <p className="text-xs text-text-tertiary font-medium">
            AURA <span className="text-text-quaternary">by</span> The Navigate Compliance Group
          </p>
          <p className="text-[10px] text-text-quaternary mt-0.5">
            © 2026 V2.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
